import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  Chip,
  IconButton,
  Collapse,
  Alert,
  CardMedia,
  CardContent,
  Divider,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  PhotoCamera as PhotoIcon,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { Report, Site, convertTimestamp } from '../../types/firebase';
import {
  approximateCoordinatesForPublicViewer,
  canUserDownloadReport,
  getReportLocationAddressDisplay,
  getReportSiteNameDisplay,
  getScannerDisplayLabel,
  shouldRedactScannerIdentity,
} from '../../utils/reportPrivacyDisplay';
import { generateEnhancedPDF } from '../../services/enhancedPdfGenerator';
import { APPROX_AREA_CIRCLE_PATH } from '../../utils/mapApproxAreaStyle';
import { normalizeCoilShiftData } from '../../utils/coilShiftData';
import CoilShiftCurvePlot from './CoilShiftCurvePlot';

interface ExtendedReport extends Report {
  uris?: Array<{ id: string; img: string }>;
  photo?: string;
  siteDetails?: any;
  [key: string]: any; // Allow for any additional fields
}

const DARK_TILES = 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png';
const ONE_MILE_METERS = 1609.34;

/**
 * Imperative circle + fitBounds: react-leaflet <Circle> can throw inside MUI Dialog (map has no
 * renderer until layout). Adding L.circle with addTo(map) after mount avoids layerPointToLatLng errors.
 */
function ApproximateMileCircleOnMap({
  lat,
  lng,
  radiusMeters,
}: {
  lat: number;
  lng: number;
  radiusMeters: number;
}) {
  const map = useMap();
  useEffect(() => {
    const circle = L.circle([lat, lng], {
      radius: radiusMeters,
      color: APPROX_AREA_CIRCLE_PATH.color,
      fillColor: APPROX_AREA_CIRCLE_PATH.fillColor,
      fillOpacity: APPROX_AREA_CIRCLE_PATH.fillOpacity,
      weight: APPROX_AREA_CIRCLE_PATH.weight,
    });
    circle.addTo(map);

    const fit = () => {
      try {
        map.invalidateSize();
        map.fitBounds(circle.getBounds(), { padding: [28, 28], maxZoom: 12 });
      } catch {
        /* map not laid out yet */
      }
    };

    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(fit);
    });
    const t2 = window.setTimeout(fit, 200);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.clearTimeout(t2);
      if (map.hasLayer(circle)) map.removeLayer(circle);
    };
  }, [map, lat, lng, radiusMeters]);
  return null;
}

interface ReportDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  report: ExtendedReport | null;
  /** Used to align approximate public coordinates with site anchors (same as Reports map). */
  sites?: Site[];
}

const ReportDetailsDialog: React.FC<ReportDetailsDialogProps> = ({ open, onClose, report, sites = [] }) => {
  const { colors } = useTheme();
  const { userProfile } = useAuth();
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imageError, setImageError] = useState<{ [key: string]: boolean }>({});
  const [mapVisible, setMapVisible] = useState(true);

  useEffect(() => {
    if (open) setMapVisible(true);
  }, [open, report?.id]);

  if (!report) return null;

  const canDownload = canUserDownloadReport(report, userProfile?.masterOwnerID);

  const handleDownloadReport = async () => {
    if (!canUserDownloadReport(report, userProfile?.masterOwnerID)) return;
    try {
      setDownloadingReport(true);
      await generateEnhancedPDF(report as any, {
        includePhotos: true,
        includeMap: true,
        reportType: 'incident',
        viewerMasterOwnerId: userProfile?.masterOwnerID,
      });
    } catch (error) {
      console.error('Failed to generate PDF report:', error);
    } finally {
      setDownloadingReport(false);
    }
  };

  const formatDate = (date: any) => {
    const d = convertTimestamp(date);
    return d.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const statusDisplay = (report as any).status === 'Skimmer Detected!' ? 'Skimmer Detected' : (report as any).status;
  const isSkimmerDetected = statusDisplay === 'Skimmer Detected';
  const isNoSkimmerDetected = statusDisplay === 'No Skimmer Detected';

  const getStatusIcon = () => {
    if (isSkimmerDetected) return <WarningIcon sx={{ color: colors.Error, fontSize: 40 }} />;
    if (isNoSkimmerDetected) return <CheckCircleIcon sx={{ color: colors.Success, fontSize: 40 }} />;
    return <AccessTimeIcon sx={{ color: colors.Warning, fontSize: 40 }} />;
  };

  const getStatusColor = () => {
    if (isSkimmerDetected) return colors.Error;
    if (isNoSkimmerDetected) return colors.Success;
    return colors.Warning;
  };

  const isManualEntry = !!(report as any).isManualEntry ||
    String((report as any).readerDetails?.siteName || '').toLowerCase() === 'manual entry' ||
    String((report as any).readerDetails?.device?.deviceType || '').toLowerCase().includes('manual entry');
  const reportTypeLabel = isManualEntry ? 'Manual Entry' : 'Device Scan';

  const anyReport = report as any;
  let images: string[] = [];
  if (anyReport.shareData?.images?.uris && Array.isArray(anyReport.shareData.images.uris)) {
    images = anyReport.shareData.images.uris.map((uri: any) => uri.img).filter((img: string) => img);
  } else if (report.uris && Array.isArray(report.uris)) {
    images = report.uris.map(uri => uri.img).filter(img => img);
  } else if (report.photo) {
    images = [report.photo];
  }

  const hasPhoto = images.length > 0;
  const notesValue = (report as any).shareData?.feedback?.value || report.message || '';
  const additionalNotesStr = String(anyReport.shareData?.additionalNotes ?? '').trim();
  const hasNotes = !!String(notesValue).trim();
  const hasDeviceModel = !!String((report as any).readerDetails?.device?.modelName || '').trim();
  const hasPreciseLocation = !!(report as any).location?.latitude && !!(report as any).location?.longitude;
  const lawEnf = (report as any).lawEnforcement;
  const lawReported = !!(lawEnf?.reported || lawEnf?.reportedToLawEnforcement);

  const getReportCoordinates = (): { lat: number | null, lng: number | null } => {
    
    // Check various possible coordinate locations
    if (anyReport.location?.latitude && anyReport.location?.longitude) {
      return { lat: anyReport.location.latitude, lng: anyReport.location.longitude };
    }
    if (anyReport.sitesDetails?.latitude && anyReport.sitesDetails?.longitude) {
      return { lat: anyReport.sitesDetails.latitude, lng: anyReport.sitesDetails.longitude };
    }
    if (anyReport.readerDetails?.device?.coordinates?.latitude && anyReport.readerDetails?.device?.coordinates?.longitude) {
      return { lat: anyReport.readerDetails.device.coordinates.latitude, lng: anyReport.readerDetails.device.coordinates.longitude };
    }
    if (anyReport.latitude && anyReport.longitude) {
      return { lat: anyReport.latitude, lng: anyReport.longitude };
    }
    
    return { lat: null, lng: null };
  };

  const rawCoords = getReportCoordinates();
  const viewerId = userProfile?.masterOwnerID;
  const redacted = shouldRedactScannerIdentity(report as Report, viewerId);
  const approx = approximateCoordinatesForPublicViewer(report as Report, viewerId, sites);
  const coords = approx
    ? { lat: approx.lat, lng: approx.lng }
    : rawCoords;

  const handleImageError = (imageUrl: string) => {
    setImageError(prev => ({ ...prev, [imageUrl]: true }));
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.03)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(34, 211, 238, 0.25)',
            borderTop: '1px solid rgba(34, 211, 238, 0.5)',
            borderLeft: '1px solid rgba(34, 211, 238, 0.4)',
            borderRadius: '20px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05), inset 0 0 20px rgba(34, 211, 238, 0.05), 0 0 15px rgba(34, 211, 238, 0.1)',
          }
        }}
      >
        <DialogTitle sx={{
          fontFamily: "'Inter', sans-serif",
          color: '#ffffff',
          fontSize: '15px',
          fontWeight: 700,
          letterSpacing: '0.3em',
          textTransform: 'uppercase',
          padding: '20px 24px',
          borderBottom: '1px solid rgba(34, 211, 238, 0.2)',
          backgroundColor: 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <Box display="flex" alignItems="center" gap={2}>
            {getStatusIcon()}
            <Box>
              <Typography variant="h6" sx={{ fontSize: 'inherit', fontWeight: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit' }}>Report Details</Typography>
              <Typography variant="caption" color={colors.TextSecondary} sx={{ display: 'block', mt: 0.25 }}>
                ID: {report.id}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ color: colors.TextSecondary }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ backgroundColor: 'transparent', padding: '24px', mt: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Top block: Site name + date + address (match mobile infoRow) */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h5" fontWeight="bold" color={colors.TextPrimary} sx={{ fontSize: '1.35rem' }}>
                  {getReportSiteNameDisplay(report as Report, viewerId)}
                </Typography>
                <Typography variant="body1" color={colors.TextSecondary}>
                  {formatDate(report.createdAt)}
                </Typography>
                <Typography variant="body2" color={colors.TextSecondary} sx={{ mt: 0.5 }}>
                  {redacted
                    ? getReportLocationAddressDisplay(report as Report, viewerId)
                    : (report as any).sitesDetails?.address ||
                      getReportLocationAddressDisplay(report as Report, viewerId) ||
                      'No address available'}
                </Typography>
                {((report as any).caseNumber || (report as any).lawEnforcement?.caseNumber) && (
                  <Typography variant="body2" color={colors.TextSecondary} sx={{ mt: 0.75 }}>
                    Case number:{' '}
                    <Box component="span" sx={{ fontWeight: 700, color: colors.TextPrimary }}>
                      {String((report as any).caseNumber ?? (report as any).lawEnforcement?.caseNumber ?? '')}
                    </Box>
                  </Typography>
                )}
              </Box>
              <Chip
                label={(report.privacy || 'private').charAt(0).toUpperCase() + (report.privacy || 'private').slice(1)}
                size="small"
                sx={{
                  backgroundColor: report.privacy === 'public' ? colors.Info : colors.Warning,
                  color: colors.Light_Shadow,
                  fontWeight: 600,
                }}
              />
            </Box>
            <Divider sx={{ borderColor: colors.Light_Shadow + '40' }} />

            {/* User Info (mobile order) */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Typography variant="subtitle1" fontWeight="bold" color={colors.TextPrimary} sx={{ opacity: 0.9 }}>
                User Info
              </Typography>
              {redacted ? (
                <>
                  <Typography variant="body2" color={colors.TextSecondary}>
                    Scanner identity is hidden for public reports from other organizations.
                  </Typography>
                  <Typography variant="body2" color={colors.TextSecondary}>
                    Display name: {getScannerDisplayLabel(report as Report, viewerId)}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body2" color={colors.TextSecondary}>Name: {report.user?.name || '—'}</Typography>
                  <Typography variant="body2" color={colors.TextSecondary}>Email: {report.user?.email || '—'}</Typography>
                  <Typography variant="body2" color={colors.TextSecondary}>Role: {report.user?.role || '—'}</Typography>
                </>
              )}
            </Box>

            {/* Last Updated By */}
            {(report as any).lastEditBy && !redacted && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Typography variant="subtitle1" fontWeight="bold" color={colors.TextPrimary} sx={{ opacity: 0.9 }}>
                  Last Updated By
                </Typography>
                <Typography variant="body2" color={colors.TextSecondary}>
                  {formatDate((report as any).lastEditBy?.createdAt)} — {(report as any).lastEditBy?.name} · {(report as any).lastEditBy?.email} · {(report as any).lastEditBy?.role}
                </Typography>
              </Box>
            )}

            {/* Skimmer Location (map) - mobile order; map show/hide like mobile */}
            {open && coords.lat && coords.lng && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold" color={colors.TextPrimary} sx={{ opacity: 0.9 }}>
                    {redacted ? 'Approximate scan area' : 'Skimmer Location'}
                  </Typography>
                  <Button size="small" variant="outlined" onClick={() => setMapVisible((v) => !v)} sx={{ borderRadius: '50px', textTransform: 'none' }}>
                    {mapVisible ? 'Hide map' : 'Show map'}
                  </Button>
                </Box>
                <Collapse in={mapVisible}>
                <Box
                  sx={{
                    height: '280px',
                    borderRadius: '10px',
                    overflow: 'hidden',
                    position: 'relative',
                    background: '#0a0a0f',
                  }}
                >
                  <MapContainer
                    key={`detail-map-${report.id}-${open}`}
                    center={[coords.lat, coords.lng]}
                    zoom={redacted ? 11 : 15}
                    zoomControl={false}
                    attributionControl={false}
                    style={{ height: '100%', width: '100%', background: '#0a0a0f' }}
                  >
                    <TileLayer url={DARK_TILES} />
                    {redacted && (
                      <ApproximateMileCircleOnMap
                        lat={coords.lat}
                        lng={coords.lng}
                        radiusMeters={ONE_MILE_METERS}
                      />
                    )}
                    {!redacted && (
                    <Marker position={[coords.lat, coords.lng]}>
                      <Popup>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {getReportSiteNameDisplay(report as Report, viewerId)}
                        </Typography>
                        <Typography variant="body2">
                          {getReportLocationAddressDisplay(report as Report, viewerId)}
                        </Typography>
                      </Popup>
                    </Marker>
                    )}
                  </MapContainer>
                  <div className="map-overlay" aria-hidden="true">
                    <div className="map-overlay-teal" />
                    <div className="map-overlay-grid" />
                  </div>
                </Box>
                </Collapse>
              </Box>
            )}

            {/* Assigned Users (mobile order) */}
            {report.location?.assignedUsers?.length > 0 && !redacted && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Typography variant="subtitle1" fontWeight="bold" color={colors.TextPrimary} sx={{ opacity: 0.9 }}>
                  Assigned Users
                </Typography>
                {report.location.assignedUsers.map((u: any, idx: number) => (
                  <Box key={idx} sx={{ pl: 1 }}>
                    <Typography variant="body2" color={colors.TextSecondary}>Name: {u.name}</Typography>
                    <Typography variant="body2" color={colors.TextSecondary}>Email: {u.email}</Typography>
                    <Typography variant="body2" color={colors.TextSecondary}>Role: {u.role}</Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* Scan Details (mobile: Skimmer Status + Scan Time + Report Type + Confirmed Skimmer) */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Typography variant="subtitle1" fontWeight="bold" color={colors.TextPrimary} sx={{ opacity: 0.9 }}>
                Scan Details
              </Typography>
              <Typography variant="body2" color={colors.TextSecondary}>
                Skimmer Status: <Box component="span" sx={{ fontWeight: 'bold', color: getStatusColor() }}>{statusDisplay}</Box>
              </Typography>
              {isSkimmerDetected && (report as any).confirmedSkimmer != null && (
                <Typography variant="body2" color={colors.TextSecondary}>
                  Confirmed Skimmer: {(report as any).confirmedSkimmer ? 'Yes' : 'No'}
                  {!redacted &&
                    (report as any).confirmedSkimmerBy &&
                    ` by ${(report as any).confirmedSkimmerBy.name}`}
                </Typography>
              )}
              <Typography variant="body2" color={colors.TextSecondary}>Scan Time: {formatDate(report.createdAt)}</Typography>
              <Typography variant="body2" color={colors.TextSecondary}>Report Type: {reportTypeLabel}</Typography>
              {(() => {
                const curvePts = normalizeCoilShiftData((report as ExtendedReport).coilShiftData);
                if (!curvePts || curvePts.length < 2) return null;
                return (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color={colors.TextSecondary} sx={{ fontWeight: 700, mb: 1 }}>
                      Scan Curve Data (Coil Shift)
                    </Typography>
                    <CoilShiftCurvePlot data={curvePts} height={150} showGrid strokeColor={colors.Gradient} />
                  </Box>
                );
              })()}
            </Box>

            {/* Evidence Checklist (mobile: only when Skimmer Detected) */}
            {isSkimmerDetected && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Typography variant="subtitle1" fontWeight="bold" color={colors.TextPrimary} sx={{ opacity: 0.9 }}>
                  Evidence Checklist
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: 18, color: hasPhoto ? colors.Success : colors.TextMuted }} />
                    <Typography variant="body2" color={colors.TextSecondary}>Photo attached</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: 18, color: hasNotes ? colors.Success : colors.TextMuted }} />
                    <Typography variant="body2" color={colors.TextSecondary}>Notes added</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: 18, color: hasDeviceModel ? colors.Success : colors.TextMuted }} />
                    <Typography variant="body2" color={colors.TextSecondary}>Device model captured</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: 18, color: hasPreciseLocation ? colors.Success : colors.TextMuted }} />
                    <Typography variant="body2" color={colors.TextSecondary}>Location captured</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: 18, color: lawReported ? colors.Success : colors.TextMuted }} />
                    <Typography variant="body2" color={colors.TextSecondary}>Reported to law enforcement</Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Severity & Actions Taken (mobile order) */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Typography variant="subtitle1" fontWeight="bold" color={colors.TextPrimary} sx={{ opacity: 0.9 }}>
                Severity & Actions Taken
              </Typography>
              <Typography variant="body2" color={colors.TextSecondary}>
                Severity: <strong>{(report as any).severity ?? 'not set'}</strong>
              </Typography>
              <Typography variant="body2" color={colors.TextSecondary}>
                Actions Taken: <strong>
                  {Array.isArray((report as any).actionsTaken) && (report as any).actionsTaken?.length
                    ? (report as any).actionsTaken.join(', ')
                    : 'none'}
                </strong>
              </Typography>
              {(report as any).actionsTakenNotes && (
                <Typography variant="body2" color={colors.TextSecondary} sx={{ mt: 0.5 }}>
                  Notes: {(report as any).actionsTakenNotes}
                </Typography>
              )}
            </Box>

            {/* Reader Information (mobile order) */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Typography variant="subtitle1" fontWeight="bold" color={colors.TextPrimary} sx={{ opacity: 0.9 }}>
                Reader Information
              </Typography>
              {redacted ? (
                <Typography variant="body2" color={colors.TextSecondary}>
                  Reader details are not shown for public reports from other organizations.
                </Typography>
              ) : (
                <>
                  <Typography variant="body2" color={colors.TextSecondary}>
                    Device Type: {(report as any).readerDetails?.device?.deviceType || '—'}
                  </Typography>
                  <Typography variant="body2" color={colors.TextSecondary}>
                    Model: {(report as any).readerDetails?.device?.modelName || '—'}
                  </Typography>
                  <Typography variant="body2" color={colors.TextSecondary}>
                    Model Number: {(report as any).readerDetails?.device?.modelNumber || '—'}
                  </Typography>
                  <Typography variant="body2" color={colors.TextSecondary}>
                    Tag Type:{' '}
                    {(report as any).readerDetails?.device?.tagType === 'nfc'
                      ? 'NFC tag'
                      : (report as any).readerDetails?.device?.tagType === 'qr'
                        ? 'QR code'
                        : (report as any).readerDetails?.device?.tagType || '—'}
                  </Typography>
                  <Typography variant="body2" color={colors.TextSecondary}>
                    Specific Location: {(report as any).readerDetails?.device?.specificLocation || (report as any).readerDetails?.device?.location || '—'}
                  </Typography>
                  {(report as any).readerDetails?.device?.qrCodeNumber && (
                    <Typography variant="body2" color={colors.TextSecondary}>
                      QR Code Number: {(report as any).readerDetails.device.qrCodeNumber}
                    </Typography>
                  )}
                  {(report as any).readerDetails?.device?.qrCodeUrl && (
                    <Box sx={{ mt: 1 }}>
                      <img
                        src={(report as any).readerDetails.device.qrCodeUrl}
                        alt="QR Code"
                        style={{ maxWidth: 120, height: 'auto' }}
                      />
                    </Box>
                  )}
                </>
              )}
            </Box>

            {/* Additional Information (mobile: Skimmer Images + Notes + admin additional notes) */}
            {(images.length > 0 || notesValue || additionalNotesStr) && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" color={colors.TextPrimary} sx={{ opacity: 0.9 }}>
                  Additional Information
                </Typography>
                {images.length > 0 && (
                  <>
                    <Typography variant="body2" color={colors.TextSecondary} sx={{ mb: 0.5 }}>Skimmer Image:</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      {images.map((imageUrl, index) => (
                        <Card
                          key={index}
                          sx={{ cursor: 'pointer', width: 160, overflow: 'hidden' }}
                          onClick={() => setSelectedImage(imageUrl)}
                        >
                          {!imageError[imageUrl] ? (
                            <CardMedia component="img" height="160" image={imageUrl} alt={`Skimmer ${index + 1}`} onError={() => handleImageError(imageUrl)} sx={{ objectFit: 'cover' }} />
                          ) : (
                            <Box sx={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: colors.Background }}>
                              <PhotoIcon sx={{ fontSize: 40, color: colors.TextMuted }} />
                            </Box>
                          )}
                          <CardContent sx={{ py: 0.5 }}>
                            <Typography variant="caption" color={colors.TextSecondary}>Image {index + 1}</Typography>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  </>
                )}
                {notesValue && (
                  <>
                    <Typography variant="body2" color={colors.TextSecondary} sx={{ mt: 1, mb: 0.5 }}>Notes:</Typography>
                    <Typography variant="body2" color={colors.TextPrimary} sx={{ whiteSpace: 'pre-wrap' }}>{notesValue}</Typography>
                  </>
                )}
                {additionalNotesStr && (
                  <>
                    <Typography variant="body2" color={colors.TextSecondary} sx={{ mt: 1, mb: 0.5 }}>Additional notes:</Typography>
                    <Typography variant="body2" color={colors.TextPrimary} sx={{ whiteSpace: 'pre-wrap' }}>{additionalNotesStr}</Typography>
                  </>
                )}
              </Box>
            )}

            {/* Law Enforcement (mobile: only when not No Skimmer Detected) */}
            {isSkimmerDetected && (report as any).lawEnforcement && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Typography variant="subtitle1" fontWeight="bold" color={colors.TextPrimary} sx={{ opacity: 0.9 }}>
                  Law Enforcement
                </Typography>
                <Typography variant="body2" color={colors.TextSecondary}>
                  Reported to law enforcement:{' '}
                  {(report as any).lawEnforcement.reported === true ||
                  (report as any).lawEnforcement.reportedToLawEnforcement === true
                    ? 'Yes'
                    : 'No'}
                </Typography>
                {(report as any).lawEnforcement.department && (
                  <Typography variant="body2" color={colors.TextSecondary}>Department: {(report as any).lawEnforcement.department}</Typography>
                )}
                {(report as any).lawEnforcement.contactName && (
                  <Typography variant="body2" color={colors.TextSecondary}>Contact Name: {(report as any).lawEnforcement.contactName}</Typography>
                )}
                {(report as any).lawEnforcement.contactPhone && (
                  <Typography variant="body2" color={colors.TextSecondary}>Contact Phone: {(report as any).lawEnforcement.contactPhone}</Typography>
                )}
                {(report as any).lawEnforcement.caseNumber && (
                  <Typography variant="body2" color={colors.TextSecondary}>Case Number: {(report as any).lawEnforcement.caseNumber}</Typography>
                )}
                {(report as any).lawEnforcement.reportedDate && (
                  <Typography variant="body2" color={colors.TextSecondary}>Reported Date: {formatDate((report as any).lawEnforcement.reportedDate)}</Typography>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ padding: '16px 24px', gap: 1, borderTop: '1px solid rgba(34, 211, 238, 0.2)', backgroundColor: 'transparent' }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              color: '#ffffff',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '50px',
              fontWeight: 'bold',
              px: 2,
              '&:hover': { background: 'rgba(255, 255, 255, 0.2)', borderColor: 'rgba(255, 255, 255, 0.25)' },
            }}
          >
            Close
          </Button>
          {canDownload ? (
          <Button
            variant="outlined"
            startIcon={downloadingReport ? <CircularProgress size={16} /> : <DownloadIcon />}
            onClick={handleDownloadReport}
            disabled={downloadingReport}
            sx={{
              color: '#ffffff',
              border: '1px solid rgba(34, 211, 238, 0.5)',
              background: 'rgba(34, 211, 238, 0.15)',
              borderRadius: '50px',
              fontWeight: 'bold',
              px: 2,
              '&:hover': { background: 'rgba(34, 211, 238, 0.25)', borderColor: '#22d3ee' },
              '&:disabled': { borderColor: 'rgba(255,255,255,0.1)', color: colors.TextMuted, background: 'rgba(255,255,255,0.05)' },
            }}
          >
            {downloadingReport ? 'Generating...' : 'Download PDF'}
          </Button>
          ) : (
          <Tooltip title="Only reports from your own company can be downloaded">
            <span>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                disabled
                sx={{
                  color: colors.TextMuted,
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '50px',
                  fontWeight: 'bold',
                  px: 2,
                }}
              >
                Download PDF
              </Button>
            </span>
          </Tooltip>
          )}
        </DialogActions>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        maxWidth="lg"
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.03)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(34, 211, 238, 0.25)',
            borderRadius: '20px',
          }
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            onClick={() => setSelectedImage(null)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              backgroundColor: colors.Dark_Shadow + '80',
              color: colors.Light_Shadow,
              '&:hover': {
                backgroundColor: colors.Dark_Shadow + 'B3',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Evidence"
              style={{
                width: '100%',
                height: 'auto',
                display: 'block'
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReportDetailsDialog;
